import {Dispatch, SetStateAction, useContext, useEffect, useState} from 'react';

import {useLocation} from 'react-router-dom';

import AdobeTargetContext, {
  initialMBoxValueState,
  TARGET_GLOBAL_MBOX
} from 'components/app/AdobeTargetProvider/AdobeTargetContext';
import {Offer, isABTestOffer, isMessageBannerOffer, isModalOffer} from 'components/app/AdobeTargetProvider/offer';
import {AdobeTargetAllMBoxValues} from 'components/app/AdobeTargetProvider/types';
import {useFeatures} from 'config/features';
import {getPrefetchABTests} from 'utils/adobe-target/abtest-prefetch';

const adobeAvailable = (adobe: any, functions: string | string[]) => {
  if (typeof adobe === 'undefined' || !adobe.target) {
    return false;
  }
  return (Array.isArray(functions) ? functions : [functions]).every(
    (functionName: string) => typeof adobe.target[functionName] === 'function'
  );
};

export const getGlobalMBOXJsonOffers = (adobe: any, params?: any): Promise<Offer[]> =>
  new Promise((resolve, reject) => {
    if (adobeAvailable(adobe, 'getOffer')) {
      adobe.target.getOffer({
        mbox: TARGET_GLOBAL_MBOX,
        params: params,
        success: function (actions: any[]) {
          resolve(getGlobalMBOXOfferContent(actions));
        },
        error: function (status: any, error: any) {
          reject(error);
        }
      });
    } else {
      resolve([]);
    }
  });

const getGlobalMBOXOfferContent = (actions: any) => {
  const offers = Array.isArray(actions) && actions.filter((action) => action.action === 'setJson');
  const content: Offer[] = Array.isArray(offers) && offers.length ? offers[0].content : [];
  return Array.isArray(content) ? content : [];
};

export const useAdobeTarget = () => useContext(AdobeTargetContext);
export const useAdobeTargetGlobalMBox = () => useAdobeTarget()[TARGET_GLOBAL_MBOX];

export const useAdobeTargetState = () =>
  useState<AdobeTargetAllMBoxValues>({
    [TARGET_GLOBAL_MBOX]: initialMBoxValueState,
    abPrefetch: {prefetch: {mboxes: []}}
  });

export const handleAdobeOffers = (
  setParameters: Dispatch<SetStateAction<AdobeTargetAllMBoxValues>>,
  adobe: any,
  params?: any,
  dependenciesStillLoading?: boolean
) =>
  getGlobalMBOXJsonOffers(adobe, params)
    .then((offers: Offer[]) => {
      const aemMessageBanners = offers.filter(isMessageBannerOffer);
      const aemModalOffer = offers.find(isModalOffer);
      const abTests = offers.filter(isABTestOffer);
      setParameters((currentParams) => ({
        ...currentParams,
        [TARGET_GLOBAL_MBOX]: {
          messageBanners: aemMessageBanners,
          modal: aemModalOffer,
          abTests: abTests,
          dependenciesStillLoading: !!dependenciesStillLoading
        }
      }));
    })
    .then(() => {
      getPrefetchABTests(adobe).then((response) => {
        setParameters((currentParams) => ({
          ...currentParams,
          abPrefetch: response
        }));
      });
    })
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.warn(`Adobe Target getJson Error: ${e.message}`);
    });

export const useSyncHistoryToTarget = (isAdobeLoaded: boolean, adobe: any) => {
  const location = useLocation();
  const {TempEnableAdobeTargetVECIntegration} = useFeatures();

  useEffect(() => {
    if (!isAdobeLoaded || !TempEnableAdobeTargetVECIntegration) {
      return;
    }
    const viewName =
      location.pathname.startsWith('#') || location.pathname.startsWith('/')
        ? location.pathname.substring(1)
        : location.pathname;
    if (viewName && typeof adobe != 'undefined' && adobe.target && typeof adobe.target.triggerView === 'function') {
      adobe.target.triggerView(viewName);
    }
  }, [TempEnableAdobeTargetVECIntegration, adobe, location.pathname, isAdobeLoaded]);
};
